<template>
  <v-sheet class="camDetailAccount">
    <resizable-page>
      <template #header>
        <section class="camDetailAccount_actions">
          <app-text-field
            v-model="query"
            class="camDetailAccount_search"
            color="dark"
            aria-autocomplete="off"
            autocomplete="off"
            outlined
            full-width
            :hide-details="true"
            placeholder="ユーザーの検索"
          />
          <v-btn class="camDetailAccount_new" @click="handleNew">
            ユーザーの新規登録</v-btn
          >
        </section>
      </template>
      <template #table="tableProps">
        <v-skeleton-loader
          v-if="loading || routeLoading || refreshLoading"
          :loading="loading || routeLoading || refreshLoading"
          type="table"
        ></v-skeleton-loader>
        <v-data-table
          v-else
          class="camDetailAccount_tbl"
          :height="tableProps.bodyHeight"
          :headers="computedHeaders"
          :items="tableItems"
          hide-default-footer
          disable-pagination
          sort-by="dateTime"
          :fixed-header="true"
        >
          <!-- Table body template  -->
          <template #item="{ item }">
            <tr @click="handleRow(item)">
              <td class="text-start">
                <tooltip>
                  <template #activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      class="camDetailAccount_tbl-name text-truncate"
                      style="max-width: 100%"
                      v-on="on"
                    >
                      {{ item.name }}
                      <span
                        v-if="item.owner"
                        class="camDetailAccount_name-owner"
                        >組織管理者</span
                      >
                    </div>
                  </template>
                  <h2>
                    {{ item.name }}
                  </h2>
                </tooltip>
              </td>
              <td class="text-start">
                <tooltip :classes="'min-width-content'">
                  <template #activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      class="camDetailAccount_tbl-registerd text-truncate"
                      style="max-width: 100%"
                      v-on="on"
                    >
                      {{ item.email }}
                    </div>
                  </template>
                  <h2>{{ item.email }}</h2>
                </tooltip>
              </td>
              <td class="text-start">
                <tooltip>
                  <template #activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      class="camDetailAccount_tbl-account text-truncate"
                      style="max-width: 100%"
                      v-on="on"
                    >
                      {{ item.authority }}
                    </div>
                  </template>
                  <h2>{{ item.authority }}</h2>
                </tooltip>
              </td>
              <td class="text-center">
                <v-btn icon @click="handleEdit(item)">
                  <span v-if="!item.owner" class="icon icon-3dots"></span>
                </v-btn>
              </td>
            </tr>
          </template>
          <!-- ----------------- -->
          <!-- Table no data -->
          <template #no-data>
            <tr style="visibility: hidden" />
          </template>
          <!-- ----------------- -->
        </v-data-table>
      </template>
    </resizable-page>

    <!-- Add user dialog -->
    <AddUserDialog
      :loading="loadingAddUser"
      :open="dialogAddUserOpen"
      title="ユーザーを追加する"
      ok-label="追加する"
      @onClose="closeDialogAddUser"
      @onOk="handleAddUserOk"
    />
    <!-- ----------------- -->

    <!-- Message dialog -->
    <clip-dialog-message
      :open="!!dialogMessageText"
      :message="dialogMessageText"
      @onClose="closeDialogMessage"
    />
    <!-- ----------------- -->

    <!-- Delete dialog -->
    <delete-dialog
      :open="dialogDeleteOpen"
      title="カメラグループからユーザーを削除する"
      @onClose="closeDialogDelete"
    >
      <div class="camDetailAccount_dialog-content">
        {{ editItem && editItem.name }} を <br />
        削除してもよろしいですか？
      </div>
      <template #actions>
        <v-btn
          class="camDetailAccount_dialog-btn camDetailAccount_dialog-cancel"
          color="text"
          @click="closeDialogDelete"
        >
          キャンセル
        </v-btn>
        <v-spacer />
        <v-btn
          class="camDetailAccount_dialog-btn camDetailAccount_dialog-delete"
          color="text"
          :loading="deleteLoading"
          @click="handleDeleteOk"
        >
          削除する
        </v-btn>
      </template>
    </delete-dialog>
    <!-- ----------------- -->
  </v-sheet>
</template>

<script>
import { MimeTypes } from '@/globals/enums';
import { mapActions, mapGetters, mapState } from 'vuex';
import AddUserDialog from '@/components/dialogs/AddUserDialog.vue';
import DeleteDialog from '@/components/dialogs/ClipDialogA.vue';
import { getCameraRoles } from '@/services/ccs.group.service';
import ClipDialogMessage from '@/components/dialogs/ClipDialogMessage.vue';

export default {
  name: 'CamDetailAccount',
  components: {
    AddUserDialog,
    DeleteDialog,
    ClipDialogMessage,
  },
  props: {
    routeLoading: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    filteredItems: {
      type: Array,
      default: () => [],
    },
    group: {
      type: Object,
      default: null,
      required: true,
    },
    searchQuery: {
      type: String,
      default: () => null,
    },
  },
  data: () => ({
    dialogMessageText: '',
    query: null,
    dialogDeleteOpen: false,
    deleteLoading: false,
    editItem: null,
    mimeTypes: MimeTypes,
    selectedItems: [],
    clipToolsOpen: false,
    refreshLoading: false,
    loadingAddUser: false,
    dialogAddUserOpen: false,
    accessMap: {
      1: 'オーナー',
      2: 'エディター',
      3: 'パトロール',
    },
    tableItems: [],
    originalTableItems: [],
  }),
  computed: {
    ...mapGetters('settings', {
      users: 'usersWithOwner',
    }),
    ...mapState({
      userList: (state) => state.settings.users,
      currentAvatarId: (state) => state.settings.currentAvatarId,
      currentCameraDirectoryId: (state) =>
        state.settings.currentCameraDirectoryId,
      groupCams: (state) => state.settings.groupCams,
    }),
    user() {
      if (!this.editItem) {
        return null;
      }
      return this.userList.find(
        (user) => String(user.id) === String(this.editItem.id)
      );
    },
    computedHeaders() {
      return [
        {
          text: '名前',
          align: 'start',
          sortable: false,
          value: 'name',
          width: '130',
          class: 'camDetailAccount_header-name',
        },
        {
          text: 'ID',
          align: 'start',
          sortable: false,
          value: 'email',
          width: '150',
        },
        {
          text: '権限',
          align: 'start',
          sortable: false,
          value: 'authority',
          width: '100',
        },
        {
          text: '編集',
          align: 'center',
          sortable: false,
          value: 'edit',
          width: '60',
        },
      ];
    },
  },
  watch: {
    dialogDeleteOpen(val) {
      val || this.closeDialogDelete();
    },
    dialogMessageText(val) {
      val || this.closeDialogMessage();
    },
    query(val) {
      this.handleQuery(val);
    },
  },
  created() {
    this.currentCategory = 3;
  },
  async mounted() {
    this.query = this.searchQuery;
    this.refreshLoading = true;
    await this.getTableItems();
    setTimeout(() => {
      this.refreshLoading = false;
    }, 500);
  },
  methods: {
    ...mapActions('settings', [
      'registerInvitedUser',
      'deleteCameraGroupAvatar',
      'fetchUserAvatarList',
    ]),
    closeDialogMessage() {
      this.dialogMessageText = null;
    },
    handleQuery(query) {
      // Handle search box
      this.$emit('update:searchQuery', query);
      this.tableItems = query
        ? this.originalTableItems.filter((c) => {
            return c.name.toLowerCase().includes(query.toLowerCase());
          })
        : this.originalTableItems;
    },
    closeDialogDelete() {
      this.dialogDeleteOpen = false;
    },
    handleNew() {
      this.dialogAddUserOpen = true;
    },
    async handleDeleteOk() {
      try {
        this.deleteLoading = true;
        await this.deleteCameraGroupAvatar({
          avatarId: this.editItem.id,
          groupId: this.group.id,
        });
        // Delete editItem on frontend
        this.$emit('deleteAccounts', this.editItem.id);
        this.tableItems = this.tableItems.filter(
          (item) => item.id !== this.editItem.id
        );
        this.originalTableItems = this.tableItems;
        this.handleQuery(this.query);
        this.closeDialogDelete();
      } catch (error) {
        console.error(error);
      } finally {
        this.deleteLoading = false;
      }
    },
    handleEdit(item) {
      this.editItem = item;
      this.dialogDeleteOpen = true;
    },
    async handleAddUserOk({ authority, email }) {
      try {
        this.loadingAddUser = true;
        const authList = [
          {
            camera_group_id: this.group.id,
            camera_group_name: this.group.title,
            authority,
          },
        ];

        await this.registerInvitedUser({
          mail_address: email,
          avatar_id: Number(this.currentAvatarId),
          camera_directory_id: Number(this.currentCameraDirectoryId),
          auth_list: authList,
        });
        await this.fetchUserAvatarList();
        this.closeDialogAddUser();
        this.dialogMessageText = '招待メールを送信しました。';
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingAddUser = false;
      }
    },
    closeDialogAddUser() {
      this.dialogAddUserOpen = false;
    },
    handleRow() {},
    async getTableItems() {
      const currentAvatarIds = this.filteredItems.map((item) => item.id);
      const result = await getCameraRoles({ avatarIds: currentAvatarIds });
      if (result && result.avatarRoleLists) {
        this.filteredItems.map(async (u) => {
          const role = result.avatarRoleLists.find(
            (role) => role.avatarId === u.id
          );
          let authority = u.owner ? '組織管理者' : '';
          if (role) {
            const policies = role.policies.find(
              (policy) => String(policy.cameraGroupId) === String(this.group.id)
            );
            if (policies && policies.policyItems) {
              const policyItem = policies.policyItems.find(
                (item) => item.name === 'CameraPolicy_Access'
              );
              authority =
                !!policyItem && policyItem.value === 1
                  ? 'カメラの操作可能'
                  : 'LIVE映像のみ閲覧可能';
            }
          }
          const newItem = {
            id: u.id,
            name: u.name,
            email: u.email,
            authority,
            owner: u.owner,
          };
          u.owner
            ? this.tableItems.unshift(newItem)
            : this.tableItems.push(newItem);
        });
        this.originalTableItems = this.tableItems;
        if (this.query) {
          this.tableItems = this.tableItems.filter((item) => {
            return item.name.toLowerCase().includes(this.query.toLowerCase());
          });
        }
      }
    },
  },
};
</script>

<style lang="scss">
.camDetailAccount {
  &_dialog {
    position: relative;

    &-cancel {
      &.theme--light.v-btn.v-btn--has-bg {
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        color: #989898;
      }
    }
    &-delete {
      &.theme--light.v-btn.v-btn--has-bg {
        color: #eb545b;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        font-weight: bold;
      }
    }
    &-btn {
      width: 120px;
      font-size: 14px !important;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.43;
      letter-spacing: normal;
      text-align: center;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.16);
    }
    &-content {
      margin: 20px 0;
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.43;
      letter-spacing: normal;
      text-align: center;
      color: #666;
    }
  }
  &_actions {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
  }
  &_name-owner {
    font-size: 12px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.75;
    letter-spacing: normal;
    text-align: left;
    color: #989898;
    margin-left: 10px;
  }
  &_new {
    font-size: 14px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.14;
    letter-spacing: normal;
    text-align: left;
    border-radius: 5px;
    width: 144px;
    height: 36px;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
    &:disabled {
      color: #ccc;
      background-color: #f3f3f3;
    }
  }
  &_header-name {
    max-width: 250px;
  }
  &_search {
    margin-bottom: 0;
    margin-top: 0;
    padding-top: 0;
    max-width: 327px;
    &.v-input {
      font-size: 18px;
      .v-messages:not(.error--text) {
        .v-messages__message {
          font-size: 12px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.5;
          letter-spacing: normal;
          text-align: left;
          color: #989898;
        }
      }
    }
    .v-text-field__slot {
      padding-left: 0;
    }

    .v-input__slot {
      min-height: 36px !important;
      margin-bottom: 2px;
      .v-input__append-inner {
        margin-top: 9px;
      }
      .v-message__message {
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.5;
        letter-spacing: normal;
        text-align: left;
        color: #989898;
      }
    }
    &.theme--light.v-input input {
      font-size: 16px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.38;
      letter-spacing: normal;
      text-align: left;
      &:-webkit-autofill,
      &:-webkit-autofill::first-line {
        font-family: 'Noto Sans JP', sans-serif;
        font-size: 16px;
        font-weight: normal;
      }
    }

    .v-text-field__details {
      padding: 0 !important;
      margin-bottom: 0 !important;
    }
  }
  &-infinit_scroll {
    text-align: center;
  }
  &.theme--light.v-sheet {
    border-radius: 0;
    box-shadow: none;
    padding: 0;
  }
  &_gap-header {
    height: 26px;
  }
  &_select {
    &-icon {
      width: 34px;
      height: 34px;
      padding: 5px 5px 5px 5px;
      background-color: #0075c2;
      border-radius: 50%;
      display: table-cell;
      vertical-align: middle;
      text-align: center;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate3d(-50%, -50%, 0);
      .icon-check {
        color: #fff;
        font-size: 25px;
        font-weight: 900;
      }
    }
  }
  &_tbl {
    &.v-data-table {
      margin-top: 15px;
      .v-data-table__wrapper {
        @include scrollBarCustom;
      }
    }
    &-registerd,
    &-account {
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      color: #4b4b4b;
    }
  }
  th {
    vertical-align: middle;
    min-width: 130px;
    span {
      font-family: 'Noto Sans JP', sans-serif;
      font-size: 12px;
      font-weight: bold;
      color: #4b4b4b;
    }
  }
  td {
    vertical-align: middle;
    max-width: 75px;
    .noThumbnailImage {
      margin: 10px;
    }
  }
  tr {
    cursor: pointer;
    &.selected {
      background-color: #f4f6fc !important;
      border-color: #f4f6fc !important;
    }
  }
  &_btn {
    margin-right: 5px;
  }
  display: flex;
  flex-direction: column;
  &_subtitle {
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.67;
    letter-spacing: normal;
    text-align: left;
    color: #989898;
  }
  &_title {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2px;
    color: var(--text-color);
    max-width: 966px;
    @media (min-width: 1280px) {
      width: calc(68vw - 339.77px);
    }
    &_text {
      font-size: 20px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
    &_type {
      color: var(--warning-color-lighten1);
      font-size: 12px;
      font-weight: 600;
    }
  }
  &_videos {
    position: relative;
  }
  .v-btn {
    &:not(.v-btn--round).v-size--x-small {
      padding-left: 8px;
      padding-right: 8px;
      &.v-btn--outlined {
        padding-left: 7px;
        padding-right: 7px;
        color: #989898;
        border-color: #989898;
      }
    }
    &:not(:last-child) {
      margin-right: 8px;
    }
  }
  & .v-skeleton-loader {
    margin-top: 10px;
  }
  & .v-skeleton-loader__table-heading,
  & .v-skeleton-loader__table-tfoot {
    display: none;
  }
}
</style>
